import AddIcon from '@mui/icons-material/Add';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HistoryIcon from '@mui/icons-material/History';
import LaptopIcon from '@mui/icons-material/Laptop';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { Grid, Menu, MenuItem } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import axios from 'axios';
import * as React from 'react';
import { Link } from 'react-router-dom';
import AuthApi from '../api/AuthApi';
import DeploymentsApi from '../api/DeploymentsApi';
import LogoSvg from '../assets/socotra-white-horiz.svg';
import {
	AuthContext,
	AuthData,
	CORE_SEARCH_ENABLED,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
} from '../context/auth';
import UserContext from '../context/userContext';
import {
	ADD_NEW_APPLICATION_BUTTON_TEXT,
	ADD_NEW_POLICYHOLDER_BUTTON_TEXT,
	CHANGE_PASSWORD_ACTION_TEXT,
	CHANGE_PASSWORD_FAILURE,
	CHANGE_PASSWORD_SUCCESS,
	HEADER_CREATE_DROPDOWN_TEXT,
	HEADER_HOME_BUTTON_TEXT,
	HEADER_PAYMENTS_BUTTON_TEXT,
	HEADER_REPORTS_BUTTON_TEXT,
	LOGOUT_ACTION_TEXT,
	LOGOUT_CONFIRM_CANCEL_ACTION_TEXT,
	LOGOUT_CONFIRM_MODAL_BODY,
	LOGOUT_CONFIRM_MODAL_TITLE,
	SWITCH_ROLES_BUTTON_LABEL,
	UI_PREVIEW_CONFIRM_CANCEL_ACTION_TEXT,
	UI_PREVIEW_CONFIRM_MODAL_BODY,
	UI_PREVIEW_CONFIRM_MODAL_BODY_CONT,
	UI_PREVIEW_CONFIRM_MODAL_TITLE,
	UI_PREVIEW_DISABLE_CONFIRM_CANCEL_ACTION_TEXT,
	UI_PREVIEW_DISABLE_CONFIRM_MODAL_BODY,
	UI_PREVIEW_DISABLE_CONFIRM_MODAL_TITLE,
} from '../data/labelsAndMessages';
import { dayjs } from '../date-util';
import { removeFrontEndIntegrations } from '../fe-plugins';
import ChangePasswordModal from './ChangePasswordModal';
import CoreSearch from './coreSearch/CoreSearch';
import HeaderUserActionsList from './HeaderUserActionsList';
import Search from './search/Search';
import { errorToast, successToast } from './Toast';

interface Props {
	showSubMenu: boolean;
	authData: AuthData;
}

interface DropdownItem {
	label: string;
	icon: JSX.Element;
	callback?: () => void;
}

const Header = (props: Props) => {
	const { userRoles, changeRole } = React.useContext(UserContext);
	const [confirmLogout, setConfirmLogout] = React.useState(false);
	const [confirmUIPreview, setConfirmUIPreview] = React.useState(false);
	const [confirmUIPreviewDisable, setConfirmUIPreviewDisable] =
		React.useState(false);
	const [confirmChangePassword, setConfirmChangePassword] =
		React.useState(false);

	const { authData } = props;
	const tenantHostname = (authData.auth.hostName + '').split('.co')[0];
	const { ssoEnabled } = authData.ssoConfig;

	const [deployDate, setDeployDate] = React.useState('');

	const { hasPermission } = authData;
	const searchEnabled =
		authData?.auth?.configProperties?.[CORE_SEARCH_ENABLED] &&
		hasPermission('search.fetch');

	React.useEffect(() => {
		const source = axios.CancelToken.source();

		const fetchDeploymentData = async () => {
			try {
				const deployData = await DeploymentsApi.fetchDeploymentInfo(
					authData.auth.authorizationToken,
					{ cancelToken: source.token },
				);

				const sortedDeployments = deployData.data.sort((a, b) => {
					return parseInt(b.deployedTimestamp) - parseInt(a.deployedTimestamp);
				});

				const timestamp = sortedDeployments[0].deployedTimestamp;

				//format: Last Deployed: June 15th, 2022 11:32:32 PST
				const parsedDate = dayjs(Number(timestamp));
				const formattedDate =
					parsedDate.format('MMM D, YYYY') +
					' at ' +
					parsedDate.format('h:mm:ss A');

				setDeployDate(formattedDate);
			} catch (error) {
				console.error(error);
			}
		};
		fetchDeploymentData();

		return () => {
			source.cancel();
		};
	}, [authData.auth.authorizationToken]);

	const logout = () => {
		removeFrontEndIntegrations(window.document);
		authData.logout();
	};

	const changePassword = async (
		oldPassword: string,
		password: string,
	): Promise<boolean> => {
		let success = false;
		try {
			await AuthApi.changeUserPassword(authData.auth.authorizationToken, {
				oldPassword,
				password,
				logout: true,
			});
			setConfirmChangePassword(false);
			successToast(CHANGE_PASSWORD_SUCCESS());
			success = true;
		} catch (e) {
			errorToast(
				e.response.data.message
					? e.response.data.message
					: CHANGE_PASSWORD_FAILURE(),
			);
		} finally {
			success && logout();
		}
		return success;
	};

	const itemArr: DropdownItem[] = [
		{
			label: `Hostname: ${tenantHostname}`,
			icon: <LaptopIcon />,
			callback: null,
		},
		{
			label: `Last deployed: ${deployDate}`,
			icon: <HistoryIcon />,
			callback: null,
		},
	];

	const actionsArr: DropdownItem[] = [
		...(!ssoEnabled
			? [
					{
						label: CHANGE_PASSWORD_ACTION_TEXT(),
						icon: <SettingsIcon />,
						callback: () => setConfirmChangePassword(true),
					},
			  ]
			: []),
		{
			label: LOGOUT_ACTION_TEXT(),
			icon: <ExitToAppIcon />,
			callback: () => setConfirmLogout(true),
		},
	];

	//checks if there is more than one user and adds the switch roles button to the dropdown
	const userRolesArr: DropdownItem[] =
		userRoles && userRoles.length > 1
			? [
					{
						label: SWITCH_ROLES_BUTTON_LABEL(),
						icon: <PersonOutlineIcon />,
						callback: changeRole,
					},
			  ]
			: [];

	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		if (!anchorEl) {
			setAnchorEl(event.currentTarget);
		} else {
			setAnchorEl(null);
		}
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const dropdownButtons = [];

	if (
		hasPermission('policy.create') &&
		hasPermission('policyholder.fetch') &&
		hasPermission('quote.fetch')
	) {
		dropdownButtons.push(
			<MenuItem
				key='NewApplication'
				id='CreateDropdown__ListItem--NewApplication'
				onClick={handleClose}
				component={Link}
				to='startNewApplication'
			>
				<Button
					startIcon={<AddIcon />}
					sx={[{ '&:hover': { backgroundColor: 'unset' } }]}
				>
					{ADD_NEW_APPLICATION_BUTTON_TEXT()}
				</Button>
			</MenuItem>,
		);
	}

	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	if (
		hasPermission('policyholder.create') &&
		hasPermission('policyholder.update')
	) {
		dropdownButtons.push(
			<MenuItem
				key='NewPolicyholder'
				id='CreateDropdown__ListItem--NewPolicyHolder'
				onClick={handleClose}
				component={Link}
				to='/policyholder/new'
			>
				<Button
					startIcon={<AddIcon />}
					sx={[{ '&:hover': { backgroundColor: 'unset' } }]}
				>
					{ADD_NEW_POLICYHOLDER_BUTTON_TEXT()}
				</Button>
			</MenuItem>,
		);
	}

	return (
		<>
			<Grid
				id='AppBar'
				container
				component='nav'
				alignItems='center'
				sx={{
					backgroundColor: 'primary.main',
					color: 'common.white',
					padding: 1.5,
					minWidth: 1024,
				}}
				wrap='nowrap'
			>
				<Grid item sx={{ flexGrow: 1 }}>
					<Grid
						container
						spacing={1}
						alignItems='center'
						wrap='nowrap'
						id='AppBar__Buttons'
					>
						<Grid item sx={{ mr: 1 }}>
							<Link to='/'>
								<LogoSvg />
							</Link>
						</Grid>
						<Grid item>
							<Button
								id='AppBar__Buttons--Home'
								color='navLink'
								component={Link}
								to='home'
							>
								{HEADER_HOME_BUTTON_TEXT()}
							</Button>
						</Grid>
						{hasPermission('report.fetch') && (
							<Grid item sx={{ mr: 1.5 }}>
								<Button
									id='AppBar__Buttons--Reports'
									color='navLink'
									component={Link}
									to='reports'
								>
									{HEADER_REPORTS_BUTTON_TEXT()}
								</Button>
							</Grid>
						)}
						{cashManagementEnabled && hasPermission('payment.fetch') && (
							<Grid item sx={{ mr: 1.5 }}>
								<Button
									id='AppBar__Buttons--Payments'
									color='navLink'
									component={Link}
									to='payments'
								>
									{HEADER_PAYMENTS_BUTTON_TEXT()}
								</Button>
							</Grid>
						)}
						{dropdownButtons.length > 0 && (
							<Grid item id='AppBar__CreateDropdown'>
								<Button
									id='AppBar__Buttons--CreateDropdown'
									variant='contained'
									color='secondary'
									onClick={handleClick}
									endIcon={<ArrowDropDown />}
								>
									{HEADER_CREATE_DROPDOWN_TEXT()}
									<Menu
										id='CreateDropdown__List'
										sx={{ mt: 2 }}
										anchorEl={anchorEl}
										open={open}
										onClose={handleClose}
										anchorOrigin={{
											vertical: 'bottom',
											horizontal: 'left',
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: 'left',
										}}
									>
										{dropdownButtons}
									</Menu>
								</Button>
							</Grid>
						)}
					</Grid>
				</Grid>
				<Grid item>{searchEnabled ? <CoreSearch /> : <Search />}</Grid>
				<Grid item>
					<HeaderUserActionsList
						items={itemArr}
						actions={[...userRolesArr, ...actionsArr]}
						origin='right'
					>
						{authData.auth.displayName}
					</HeaderUserActionsList>
				</Grid>
			</Grid>
			<Dialog open={confirmLogout}>
				<DialogTitle>{LOGOUT_CONFIRM_MODAL_TITLE()}</DialogTitle>
				<DialogContent>
					<DialogContentText>{LOGOUT_CONFIRM_MODAL_BODY()}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button color='neutralButton' onClick={() => setConfirmLogout(false)}>
						{LOGOUT_CONFIRM_CANCEL_ACTION_TEXT()}
					</Button>
					<Button color='primary' variant='contained' onClick={logout}>
						{LOGOUT_ACTION_TEXT()}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={confirmUIPreview}>
				<DialogTitle>{UI_PREVIEW_CONFIRM_MODAL_TITLE()}</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>
						{UI_PREVIEW_CONFIRM_MODAL_BODY()}
					</DialogContentText>
					<DialogContentText>
						{UI_PREVIEW_CONFIRM_MODAL_BODY_CONT()}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						color='neutralButton'
						onClick={() => setConfirmUIPreview(false)}
					>
						{UI_PREVIEW_CONFIRM_CANCEL_ACTION_TEXT()}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={confirmUIPreviewDisable}>
				<DialogTitle>{UI_PREVIEW_DISABLE_CONFIRM_MODAL_TITLE()}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{UI_PREVIEW_DISABLE_CONFIRM_MODAL_BODY()}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						color='neutralButton'
						onClick={() => {
							setConfirmUIPreviewDisable(false);
						}}
					>
						{UI_PREVIEW_DISABLE_CONFIRM_CANCEL_ACTION_TEXT()}
					</Button>
				</DialogActions>
			</Dialog>
			{!ssoEnabled ? (
				<ChangePasswordModal
					open={confirmChangePassword}
					onConfirm={changePassword}
					onCancel={() => setConfirmChangePassword(false)}
				/>
			) : null}
		</>
	);
};

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => <Component {...props} authData={authData} />}
		</AuthContext.Consumer>
	);
}

export default withContext(Header);
